import { createClientI18n } from '@v1/shared';
import type { User } from '@v1/shared';
import * as Sentry from '@sentry/react';
import type { DehydratedState } from '@tanstack/react-query';
import { hydrateRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { App } from './app/App.js';
import { AppProviders } from './app/providers/AppProviders.js';
import { createRoutes } from './app/routes.js';
import '@v1/ui/styles.css';

declare global {
	interface Window {
		__QUERY_STATE__?: DehydratedState;
		__TRANSLATIONS__?: Record<string, unknown>;
		__USER__?: User | null;
		__FLAGS__?: Record<string, boolean>;
	}
}

const sentryDsn = document.body.dataset.sentryDsn;
if (sentryDsn) {
	Sentry.init({
		dsn: sentryDsn,
		tracesSampleRate: 0.1,
		integrations: [Sentry.browserTracingIntegration()],
	});
}

const IDENTIFIER_PREFIX = 'v1-';
const routePrefix = document.body.dataset.routePrefix || '/';
const locale = document.documentElement.lang || 'en-US';
const translations = window.__TRANSLATIONS__ ?? {};
const dehydratedState = window.__QUERY_STATE__;

const router = createBrowserRouter(createRoutes(routePrefix));

function routerReady(): Promise<void> {
	if (router.state.initialized) return Promise.resolve();
	return new Promise((resolve) => {
		const unsub = router.subscribe((state) => {
			if (state.initialized) {
				unsub();
				resolve();
			}
		});
	});
}

Promise.all([createClientI18n(locale, translations), routerReady()]).then(([i18nInstance]) => {
	hydrateRoot(
		document,
		<App locale={locale} routePrefix={routePrefix}>
			<AppProviders
				user={window.__USER__ ?? null}
				flags={window.__FLAGS__ ?? {}}
				i18nInstance={i18nInstance}
				dehydratedState={dehydratedState}
			>
				<RouterProvider router={router} />
			</AppProviders>
		</App>,
		{
			identifierPrefix: IDENTIFIER_PREFIX,
			onRecoverableError(error: unknown) {
				console.warn('Hydration recovered:', error);
			},
		},
	);
});
